import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { useNotification } from '../../hooks/useNotification';
import { api } from '../../api/client';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  link?: string;
  isRead: boolean;
  createdAt: string;
}

interface HeaderProps {
  onMenuToggle: () => void;
}

export default function Header({ onMenuToggle }: HeaderProps) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { addNotification } = useNotification();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTarget, setSearchTarget] = useState<'constructions' | 'requests' | 'companies'>('constructions');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    navigate(`/${searchTarget}?search=${encodeURIComponent(q)}`);
    setSearchQuery('');
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const data = await api.get<{ items: Notification[]; unreadCount: number }>('/api/notifications?limit=10');
      setNotifications(Array.isArray(data?.items) ? data.items : []);
      setUnreadCount(data?.unreadCount ?? 0);
    } catch {
      // Silently fail
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await api.put(`/api/notifications/${id}/read`, {});
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // Silently fail
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.put('/api/notifications/read-all', {});
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      // Silently fail
    }
  };

  const isSystemAlert = (notification: Notification) => {
    const keyword = `${notification.type} ${notification.title} ${notification.message}`.toLowerCase();
    const alertKeywords = [
      'alert',
      'warning',
      'system_alert',
      'deadline',
      'expired',
      'urgent',
      '期限',
      '期限切れ',
      '車検',
      '警告',
      '重要',
      '至急',
    ];
    return alertKeywords.some((k) => keyword.includes(k));
  };

  const systemAlerts = notifications.filter(isSystemAlert);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'mention':
        return '💬';
      case 'task':
        return '📋';
      case 'request':
      case 'request_assigned':
        return '📩';
      case 'construction':
        return '🔨';
      case 'board':
        return '📢';
      case 'survey':
        return '📊';
      case 'calendar':
        return '📅';
      case 'daily_report':
        return '📝';
      default:
        return '🔔';
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'たった今';
    if (minutes < 60) return `${minutes}分前`;
    if (hours < 24) return `${hours}時間前`;
    if (days < 7) return `${days}日前`;
    return date.toLocaleDateString('ja-JP');
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch {
      addNotification({ type: 'error', title: 'ログアウトに失敗しました' });
    }
  };

  return (
    <header className="h-16 bg-white dark:bg-gray-800 border-b dark:border-gray-700 flex items-center justify-between px-3 sm:px-4 md:px-6 gap-2">
      {/* 左側：ハンバーガーメニュー + 検索 */}
      <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
        {/* ハンバーガーメニューボタン（モバイルのみ表示） */}
        <button
          onClick={onMenuToggle}
          className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors md:hidden"
          aria-label="メニューを開く"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* 検索（タブレット以上で表示） */}
        <form onSubmit={handleSearch} className="hidden sm:flex flex-1 max-w-lg items-center gap-1">
          <select
            value={searchTarget}
            onChange={(e) => setSearchTarget(e.target.value as 'constructions' | 'requests' | 'companies')}
            className="h-9 px-2 bg-gray-100 dark:bg-gray-700 rounded-l-lg text-xs border-0 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="constructions">工事</option>
            <option value="requests">要請</option>
            <option value="companies">企業</option>
          </select>
          <div className="relative flex-1">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="検索..."
              className="w-full h-9 pl-9 pr-4 bg-gray-100 dark:bg-gray-700 rounded-r-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </form>
      </div>

      {/* 右側アクション */}
      <div className="flex items-center gap-1.5 sm:gap-4 shrink-0">
        {/* テーマ切り替え */}
        <button
          onClick={toggleTheme}
          className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          title={theme === 'light' ? 'ダークモードに切り替え' : 'ライトモードに切り替え'}
        >
          {theme === 'light' ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          )}
        </button>

        {/* 通知 */}
        <div className="relative">
          <button
            onClick={() => {
              setShowNotifications(!showNotifications);
              setShowProfileMenu(false);
            }}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors relative"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs font-medium rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {showNotifications && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowNotifications(false)} />
              <div className="absolute right-0 top-full mt-2 w-[min(20rem,calc(100vw-1rem))] sm:w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700 z-20 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                  <h3 className="font-semibold text-gray-900 dark:text-white">通知</h3>
                  {unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllAsRead}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      すべて既読
                    </button>
                  )}
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {systemAlerts.length > 0 && (
                    <div className="px-4 py-3 border-b dark:border-gray-700 bg-red-50 dark:bg-red-900/20">
                      <p className="text-xs font-semibold text-red-700 dark:text-red-300 mb-2">
                        システム警告メッセージ
                      </p>
                      <div className="space-y-2">
                        {systemAlerts.slice(0, 3).map((alert) => (
                          <button
                            key={`alert-${alert.id}`}
                            onClick={() => {
                              handleMarkAsRead(alert.id);
                              if (alert.link) {
                                navigate(alert.link);
                                setShowNotifications(false);
                              }
                            }}
                            className="w-full text-left rounded-md border border-red-200 dark:border-red-700 px-3 py-2 bg-white/90 dark:bg-red-950/40 hover:bg-white dark:hover:bg-red-950/60 transition-colors"
                          >
                            <p className="text-xs font-medium text-red-800 dark:text-red-200 truncate">
                              {alert.title}
                            </p>
                            <p className="text-xs text-red-700/90 dark:text-red-300/90 line-clamp-2">
                              {alert.message}
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-gray-500">
                      通知はありません
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <div
                        key={notification.id}
                        onClick={() => {
                          handleMarkAsRead(notification.id);
                          if (notification.link) {
                            navigate(notification.link);
                            setShowNotifications(false);
                          }
                        }}
                        className={`px-4 py-3 border-b dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                          !notification.isRead ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <span className="text-xl">{getNotificationIcon(notification.type)}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {notification.title}
                            </p>
                            <p className="text-xs text-gray-500 line-clamp-2">{notification.message}</p>
                            <p className="text-xs text-gray-400 mt-1">{formatTimeAgo(notification.createdAt)}</p>
                          </div>
                          {!notification.isRead && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <Link
                  to="/notifications"
                  onClick={() => setShowNotifications(false)}
                  className="block px-4 py-3 text-center text-sm text-blue-600 hover:bg-gray-50 dark:hover:bg-gray-700 border-t dark:border-gray-700"
                >
                  サイドメニューの通知一覧を見る
                </Link>
              </div>
            </>
          )}
        </div>

        {/* プロフィール */}
        <div className="relative">
          <button
            onClick={() => {
              setShowProfileMenu(!showProfileMenu);
              setShowNotifications(false);
            }}
            className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            {user?.avatarUrl ? (
              <img src={user.avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                {user?.name?.charAt(0) || 'U'}
              </div>
            )}
            <span className="hidden sm:inline text-sm font-medium text-gray-700 dark:text-gray-200">{user?.name}</span>
            <svg className="hidden sm:block w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showProfileMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowProfileMenu(false)} />
              <div className="absolute right-0 top-full mt-2 w-[min(14rem,calc(100vw-1rem))] sm:w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700 py-1 z-20">
                <div className="px-4 py-3 border-b dark:border-gray-700">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{user?.name}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                  <p className="text-xs text-blue-600 mt-1">{user?.role === 'admin' ? '管理者' : user?.role}</p>
                </div>
                <Link
                  to="/profile"
                  onClick={() => setShowProfileMenu(false)}
                  className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <span>👤</span>
                  プロフィール設定
                </Link>
                <Link
                  to="/change-password"
                  onClick={() => setShowProfileMenu(false)}
                  className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <span>🔑</span>
                  パスワード変更
                </Link>
                <Link
                  to="/notifications"
                  onClick={() => setShowProfileMenu(false)}
                  className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <span>🔔</span>
                  通知設定
                </Link>
                <div className="border-t dark:border-gray-700 my-1" />
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <span>🚪</span>
                  ログアウト
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
